import { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Send, MessageSquare, Mail, Sparkles, User as UserIcon, Phone, Inbox as InboxIcon } from "lucide-react";

const ROLE_COLOR = {
  visitor: { dot: "var(--n-text-sec)", label: "VISITOR" },
  ai: { dot: "var(--n-success)", label: "AI" },
  admin: { dot: "var(--n-blue)", label: "YOU" },
};

const CHANNEL_ICON = { chat: Sparkles, sms: MessageSquare, email: Mail };

export default function Messages() {
  const { activeTenant } = useTenant();
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const loadConversations = useCallback(async () => {
    if (!activeTenant) return;
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
      if (!selectedId && data.length) setSelectedId(data[0].id);
    } catch {}
  }, [activeTenant?.id, selectedId]);  // eslint-disable-line

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/conversations/${id}`);
      setDetail(data);
      // mark as read
      await api.post(`/conversations/${id}/read`).catch(() => {});
    } catch {}
  }, []);

  useEffect(() => {
    loadConversations();
  }, [activeTenant?.id]);  // eslint-disable-line

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  // Poll list + detail every 5s
  useEffect(() => {
    const t = setInterval(() => {
      loadConversations();
      if (selectedId) loadDetail(selectedId);
    }, 5000);
    return () => clearInterval(t);
  }, [selectedId, loadConversations, loadDetail]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [detail?.messages?.length]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !selectedId || busy) return;
    setBusy(true);
    try {
      await api.post(`/conversations/${selectedId}/reply`, { body: reply });
      setReply("");
      await loadDetail(selectedId);
      await loadConversations();
    } finally {
      setBusy(false);
    }
  };

  if (!activeTenant) return <div className="p-10 text-[var(--n-text-muted)]">Select a tenant.</div>;

  return (
    <div className="flex h-[calc(100vh-56px)]" data-testid="messages-page">
      {/* Conversation list */}
      <aside className="w-[340px] border-r border-[var(--n-border)] flex flex-col">
        <div className="p-4 border-b border-[var(--n-border)]">
          <div className="n-label">Inbox · {activeTenant.name}</div>
          <h1 className="font-cab font-black text-2xl tracking-tight mt-1">Message Center</h1>
        </div>
        <div className="flex-1 overflow-auto" data-testid="conversations-list">
          {conversations.length === 0 && (
            <div className="p-8 text-center">
              <InboxIcon size={28} className="mx-auto text-[var(--n-text-muted)] mb-3" />
              <div className="text-sm text-[var(--n-text-muted)] font-mono-plex">
                No conversations yet. Chat with the {activeTenant.name} site widget to start one.
              </div>
            </div>
          )}
          {conversations.map((c) => {
            const Icon = CHANNEL_ICON[c.channel] || MessageSquare;
            const name = c.visitor?.name || "Anonymous visitor";
            const active = c.id === selectedId;
            return (
              <button
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                data-testid={`convo-${c.id}`}
                className={`w-full text-left px-4 py-3 border-l-2 border-b border-[var(--n-border)] transition-colors duration-150 ${
                  active ? "bg-[#101015] border-l-[var(--n-blue)]" : "border-l-transparent hover:bg-[#0c0c10]"
                }`}
              >
                <div className="flex items-start gap-2">
                  <Icon size={14} className="text-[var(--n-text-muted)] mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-cab font-bold text-sm truncate">{name}</div>
                      {c.unread_admin_count > 0 && (
                        <span
                          className="text-[10px] font-mono-plex bg-[var(--n-blue)] text-white px-1.5 py-0.5 rounded-full"
                          data-testid={`convo-unread-${c.id}`}
                        >
                          {c.unread_admin_count}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--n-text-sec)] mt-0.5 line-clamp-2">
                      {c.last_message_preview || "—"}
                    </div>
                    <div className="text-[10px] font-mono-plex text-[var(--n-text-muted)] mt-1 uppercase">
                      {c.channel} · {timeAgo(c.last_message_at)}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      {/* Conversation thread */}
      <section className="flex-1 flex flex-col min-w-0">
        {!detail ? (
          <div className="flex-1 flex items-center justify-center text-[var(--n-text-muted)] text-sm font-mono-plex">
            Select a conversation
          </div>
        ) : (
          <>
            <div className="border-b border-[var(--n-border)] p-4 flex items-center justify-between">
              <div>
                <div className="font-cab font-black text-xl">
                  {detail.conversation.visitor?.name || "Anonymous visitor"}
                </div>
                <div className="flex items-center gap-3 text-xs font-mono-plex text-[var(--n-text-muted)] mt-1">
                  {detail.conversation.visitor?.email && (
                    <span className="flex items-center gap-1"><Mail size={11} /> {detail.conversation.visitor.email}</span>
                  )}
                  {detail.conversation.visitor?.phone && (
                    <span className="flex items-center gap-1"><Phone size={11} /> {detail.conversation.visitor.phone}</span>
                  )}
                  <span className="n-chip">{detail.conversation.channel}</span>
                  {detail.conversation.lead_id && (
                    <a
                      href={`/admin/leads`}
                      className="text-[var(--n-blue)] underline underline-offset-2"
                      data-testid="convo-lead-link"
                    >
                      attached to lead →
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-auto p-6 space-y-3 bg-[#0a0a0a]" data-testid="convo-thread">
              {detail.messages.map((m) => {
                const meta = ROLE_COLOR[m.sender_role] || ROLE_COLOR.visitor;
                const right = !m.from_visitor;
                return (
                  <div key={m.id} className={`flex ${right ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[78%] ${right ? "items-end" : "items-start"} flex flex-col gap-1`}>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: meta.dot }} />
                        <span className="n-label">{meta.label}{m.sender_name && m.sender_role !== "visitor" ? ` · ${m.sender_name}` : ""}</span>
                      </div>
                      <div
                        className={`p-3 border ${
                          right
                            ? m.sender_role === "ai"
                              ? "border-[var(--n-success)] bg-[#001a0c]"
                              : "border-[var(--n-blue)] bg-[#0a1530]"
                            : "border-[var(--n-border)] bg-[var(--n-surface)]"
                        } text-sm whitespace-pre-wrap`}
                      >
                        {m.body}
                      </div>
                      <div className="text-[10px] font-mono-plex text-[var(--n-text-muted)]">
                        {new Date(m.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <form onSubmit={sendReply} className="border-t border-[var(--n-border)] p-4 flex gap-2" data-testid="reply-form">
              <input
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                className="n-input flex-1"
                placeholder={`Reply as you (${activeTenant.name})…`}
                data-testid="reply-input"
              />
              <button type="submit" disabled={busy} className="n-btn-blue n-btn flex items-center gap-2 disabled:opacity-50" data-testid="reply-send">
                <Send size={14} /> {busy ? "SENDING…" : "SEND"}
              </button>
            </form>
            <div className="px-4 pb-3 text-[10px] font-mono-plex text-[var(--n-text-muted)]">
              {detail.conversation.channel === "chat"
                ? "Visitor will see your reply in their site chat widget within 5s."
                : "SMS send goes through Twilio when keys are configured."}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
